import { useState } from 'react'
import AuthenticityBadge, { AuthenticityIndicator, calculateAuthenticityScore } from './AuthenticityBadge'
import MapModal from './MapModal'

export default function AuthenticityBadgeDemo() {
  const [mapModalOpen, setMapModalOpen] = useState(false)

  // Sample location data
  const sampleLocation = {
    tier2: 'Financial District',
    tier1: 'San Francisco',
    state: 'California',
    country: 'United States',
    latitude: 37.7749,
    longitude: -122.4194
  }

  // Test cases with different authenticity scores
  const testCases: { name: string; data: Parameters<typeof calculateAuthenticityScore>[0] }[] = [
    {
      name: '🟢 Perfect - Verified Authentic (100)',
      data: {
        timeSinceCapture: 30000, // 30 seconds
        locationMatch: true,
        deviceMatch: true,
        hasMetadata: true,
        fileSize: 2048000, // 2MB
        screenDetection: { isScreen: false, confidence: 0 },
        mlConfidence: 98,
        communityValidated: true
      }
    },
    {
      name: '🟢 Excellent - Verified (95)',
      data: {
        timeSinceCapture: 300000, // 5 minutes
        locationMatch: true,
        deviceMatch: true,
        hasMetadata: true,
        fileSize: 1524000,
        screenDetection: { isScreen: false, confidence: 0 },
        mlConfidence: 92
      }
    },
    {
      name: '🟡 Good - Likely Authentic (85)',
      data: {
        timeSinceCapture: 3600000, // 1 hour
        locationMatch: true,
        deviceMatch: true,
        hasMetadata: true,
        fileSize: 1024000,
        mlConfidence: 85
      }
    },
    {
      name: '🟡 Decent - Likely Authentic (75)',
      data: {
        timeSinceCapture: 7200000, // 2 hours
        locationMatch: true,
        deviceMatch: true,
        hasMetadata: false,
        fileSize: 512000,
        mlConfidence: 75
      }
    },
    {
      name: '🟠 Uncertain (55)',
      data: {
        timeSinceCapture: 86400000 * 2, // 2 days
        locationMatch: false,
        deviceMatch: true,
        hasMetadata: false,
        fileSize: 256000,
        mlConfidence: 60
      }
    },
    {
      name: '🔴 Suspicious (35)',
      data: {
        timeSinceCapture: 86400000 * 3, // 3 days
        locationMatch: false,
        deviceMatch: false,
        hasMetadata: false,
        fileSize: 30000,
        screenDetection: { isScreen: true, confidence: 80 },
        mlConfidence: 40
      }
    },
    {
      name: '🔴 Very Suspicious (15)',
      data: {
        timeSinceCapture: 86400000 * 7, // 7 days
        locationMatch: false,
        deviceMatch: false,
        hasMetadata: false,
        fileSize: 15000,
        screenDetection: { isScreen: true, confidence: 95 },
        mlConfidence: 20
      }
    }
  ]

  const indicators = testCases.map(tc => ({
    name: tc.name,
    indicator: calculateAuthenticityScore(tc.data)
  }))

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">
            Authenticity Badge System
          </h1>
          <p className="text-gray-600">
            Testing different authenticity scores and visual indicators
          </p>
        </div>

        {/* Size Variants */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Size Variants (Verified Badge)
          </h2>

          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-24">Small:</span>
              <AuthenticityBadge
                authenticity={indicators[0].indicator}
                size="small"
                showScore={true}
                expandable={false}
              />
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-24">Medium:</span>
              <AuthenticityBadge
                authenticity={indicators[0].indicator}
                size="medium"
                showScore={true}
                expandable={false}
              />
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-24">Large:</span>
              <AuthenticityBadge
                authenticity={indicators[0].indicator}
                size="large"
                showScore={true}
                expandable={false}
              />
            </div>
          </div>
        </div>

        {/* All Score Levels */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
          <h2 className="text-xl font-bold text-gray-900">
            All Authenticity Levels
          </h2>

          {indicators.map((item, index) => (
            <div key={index} className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">
                {item.name}
              </h3>
              <AuthenticityBadge
                authenticity={item.indicator}
                size="medium"
                showScore={true}
                expandable={true}
                onViewMap={() => setMapModalOpen(true)}
              />
            </div>
          ))}
        </div>

        {/* Post Integration Example */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="text-xl font-bold text-gray-900">
            Post Integration Example
          </h2>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Mock Post Header */}
            <div className="p-4 flex items-center gap-3 border-b border-gray-200">
              <div className="w-10 h-10 bg-gradient-to-br from-onrecord-orange to-onrecord-orange-dark rounded-full" />
              <div>
                <p className="font-semibold text-gray-900">John Doe</p>
                <p className="text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>

            {/* Mock Post Image */}
            <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <p className="text-gray-500 font-medium">Post Image</p>
            </div>

            {/* Authenticity Badge */}
            <div className="p-4">
              <AuthenticityBadge
                authenticity={indicators[1].indicator} // Excellent score
                size="medium"
                showScore={true}
                expandable={true}
                onViewMap={() => setMapModalOpen(true)}
              />
            </div>

            {/* Mock Post Caption */}
            <div className="px-4 pb-4">
              <p className="text-sm text-gray-900">
                <span className="font-semibold">John Doe</span> Beautiful sunset
                at the beach! 🌅
              </p>
              <button
                onClick={() => setMapModalOpen(true)}
                className="text-sm text-gray-500 mt-2 hover:underline flex items-center gap-1"
              >
                📍 San Francisco, California
              </button>
            </div>
          </div>
        </div>

        {/* Integration Code Example */}
        <div className="bg-gray-900 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-white mb-4">
            Integration Code
          </h2>
          <pre className="text-sm text-green-400 overflow-x-auto">
{`import AuthenticityBadge from '@/components/AuthenticityBadge'
import { contentAuthService } from '@/services/ContentAuthenticationService'

// In your Post component:
const [mapModalOpen, setMapModalOpen] = useState(false)

// Analyze authenticity when creating post
const authenticityResult = await contentAuthService.analyzeMediaAuthenticity({
  fileEXIF: metadata,
  uploadLocation: location,
  uploadTimestamp: Date.now(),
  uploaderDevice: navigator.userAgent,
  timeSinceCapture: Date.now() - captureTimestamp,
  fileSize: file.size,
  mediaType: file.type
})

// Display badge in post
<AuthenticityBadge
  authenticity={post.authenticityDetection?.rating}
  size="medium"
  showScore={true}
  expandable={true}
  onViewMap={() => setMapModalOpen(true)}
/>`}
          </pre>
        </div>
      </div>

      {/* Map Modal */}
      <MapModal
        isOpen={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        location={sampleLocation}
      />
    </div>
  )
}
